import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Link,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Tooltip, CardContent, Button, Paper, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Collapse
} from '@mui/material';
import {
  ArrowUp, ArrowDown, Calculator, Upload, Trash2, FileText, RefreshCw, Edit
} from 'lucide-react';
import axios from 'axios';
import { styled, alpha } from '@mui/material/styles';
import { 
  fadeInUp, GlassmorphicCard, SectionTitle
} from '../styles/sharedStyles';

const StatCard = styled(GlassmorphicCard)(({ theme, color }) => ({
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: `radial-gradient(circle at center, ${alpha(color || theme.palette.primary.main, 0.15)} 0%, transparent 70%)`,
    transform: 'translate(30%, -30%)',
    zIndex: 0,
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(10px)',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    padding: '16px',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: 600,
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  }
}));

const Tax = ({ apiBaseUrl, token }) => {
  const [tab, setTab] = useState(0);
  const [inflow, setInflow] = useState([]);
  const [outflow, setOutflow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taxResult, setTaxResult] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxYears, setTaxYears] = useState([]);
  const [selectedTaxYear, setSelectedTaxYear] = useState('');
  const [rateLoading, setRateLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [editForm, setEditForm] = useState({
    type: 'inflow',
    date: '',
    currency: 'PLN',
    amount: '',
    description: '',
    rate: '',
    pdf: null,
  });

  const [form, setForm] = useState({
    type: 'inflow',
    date: new Date().toISOString().split('T')[0],
    currency: 'PLN',
    amount: '',
    description: '',
    rate: '',
    pdf: null,
  });

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inflowTotal, setInflowTotal] = useState(0);
  const [outflowTotal, setOutflowTotal] = useState(0);
  const [formExpanded, setFormExpanded] = useState(false);

  const currencyFlag = (currency) => {
    const c = (currency || '').toUpperCase();
    if (c === 'PLN') return '🇵🇱';
    if (c === 'EUR') return '🇪🇺';
    if (c === 'USD') return '🇺🇸';
    return '';
  };

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inRes, outRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/api/tax/transactions?type=inflow&page=${page}&limit=${rowsPerPage}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${apiBaseUrl}/api/tax/transactions?type=outflow&page=${page}&limit=${rowsPerPage}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);
      setInflow(inRes.data?.items || []);
      setOutflow(outRes.data?.items || []);
      setInflowTotal(inRes.data?.total || 0);
      setOutflowTotal(outRes.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token, page, rowsPerPage]);

  const fetchTax = useCallback(async (year) => {
    const y = year || selectedTaxYear;
    if (!y) return;
    setTaxLoading(true);
    try {
      const res = await axios.get(`${apiBaseUrl}/api/tax/calculate?year=${encodeURIComponent(y)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxResult(res.data);
    } catch (err) {
      console.error('Tax calc error:', err);
    } finally {
      setTaxLoading(false);
    }
  }, [apiBaseUrl, token, selectedTaxYear]);

  const fetchTaxYears = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/api/tax/years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const years = res.data.years || [];
      setTaxYears(years);
      if (years.length && !selectedTaxYear) {
        const latest = years[years.length - 1];
        setSelectedTaxYear(latest);
        fetchTax(latest);
      }
    } catch (err) {
      console.error('Tax years error:', err);
    }
  }, [apiBaseUrl, token, fetchTax, selectedTaxYear]);

  const fetchRate = useCallback(async () => {
    if (!form.date || !form.currency || form.currency.toUpperCase() === 'PLN') return;
    setRateLoading(true);
    try {
      const res = await axios.get(`${apiBaseUrl}/api/tax/rate?date=${form.date}&currency=${form.currency}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForm((prev) => ({ ...prev, rate: res.data.rate }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch NBP rate');
    } finally {
      setRateLoading(false);
    }
  }, [apiBaseUrl, token, form.date, form.currency]);

  useEffect(() => {
    fetchTransactions();
    fetchTaxYears();
  }, [fetchTransactions, fetchTaxYears]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('type', form.type);
    data.append('date', form.date);
    data.append('currency', form.currency);
    data.append('amount', String(form.amount));
    data.append('description', form.description);
    if (form.rate) data.append('rate', String(form.rate));
    if (form.pdf) data.append('pdf_file', form.pdf);

    try {
      await axios.post(`${apiBaseUrl}/api/tax/transactions`, data, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setForm({
        type: 'inflow',
        date: new Date().toISOString().split('T')[0],
        currency: 'PLN',
        amount: '',
        description: '',
        rate: '',
        pdf: null,
      });
      setPage(1);
      fetchTransactions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save transaction');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiBaseUrl}/api/tax/transactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPage(1);
      fetchTransactions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const handleAutoLink = async () => {
    try {
      const res = await axios.post(`${apiBaseUrl}/api/tax/autolink-pdfs`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPage(1);
      fetchTransactions();
      alert(`Linked ${res.data.linked} PDFs (scanned ${res.data.scanned})`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to auto-link PDFs');
    }
  };

  const openEdit = (tx) => {
    setEditingTx(tx);
    setEditForm({
      type: tx.type,
      date: tx.date,
      currency: tx.currency,
      amount: String(tx.amount),
      description: tx.description || '',
      rate: String(tx.rate),
      pdf: null,
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingTx) return;
    const data = new FormData();
    data.append('type', editForm.type);
    data.append('date', editForm.date);
    data.append('currency', editForm.currency);
    data.append('amount', String(editForm.amount));
    data.append('description', editForm.description);
    if (editForm.rate) data.append('rate', String(editForm.rate));
    if (editForm.pdf) data.append('pdf_file', editForm.pdf);

    try {
      await axios.put(`${apiBaseUrl}/api/tax/transactions/${editingTx.id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setEditOpen(false);
      setEditingTx(null);
      setPage(1);
      fetchTransactions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update transaction');
    }
  };

  const renderTable = (rows, type, total) => {
    const startIdx = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
    const endIdx = Math.min(page * rowsPerPage, total);
    const hasMore = page * rowsPerPage < total;

    return (
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">PLN</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>PDF</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  No transactions
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.date}</TableCell>
                <TableCell>
                  <Chip
                    label={`${currencyFlag(r.currency)} ${r.currency}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.85)',
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell align="right">{r.amount.toFixed(2)}</TableCell>
                <TableCell align="right">{r.rate.toFixed(4)}</TableCell>
                <TableCell align="right"><strong>{r.pln_value.toFixed(2)}</strong></TableCell>
                <TableCell>{r.description || '-'}</TableCell>
                <TableCell>
                  {r.pdf_name ? (
                    <Link href={`${apiBaseUrl}/api/tax/pdf/${r.pdf_name}`} target="_blank" underline="hover">
                      <FileText size={18} />
                    </Link>
                  ) : '-'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#00d4ff' }}>
                      <Edit size={16} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(r.id)} sx={{ color: '#ff4444' }}>
                      <Trash2 size={16} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Showing {startIdx}-{endIdx} of {total}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.15)' }}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.15)' }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </StyledTableContainer>
    );
  };

  return (
    <>
      <Box>
      <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': { background: (t) => t.palette.primary.main, height: '3px' },
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': { color: (t) => t.palette.primary.main },
            },
          }}
        >
          <Tab icon={<Upload size={18} />} iconPosition="start" label="Transfers Ledger" />
          <Tab icon={<Calculator size={18} />} iconPosition="start" label="Tax Intelligence" />
        </Tabs>
      </Box>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1.5, alignItems: 'center' }}>
            <Button variant="outlined" onClick={handleAutoLink} startIcon={<FileText size={16} />}>
              Auto-link PDFs
            </Button>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows</InputLabel>
              <Select value={rowsPerPage} label="Rows" onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={4}>
              <StatCard color="#00ff88">
                <CardContent>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>Total Inflow</Typography>
                  <Typography variant="h4" sx={{ color: '#00ff88', fontWeight: 700 }}>
                    {inflow.reduce((s, r) => s + r.pln_value, 0).toFixed(2)} PLN
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard color="#ff4444">
                <CardContent>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>Total Outflow</Typography>
                  <Typography variant="h4" sx={{ color: '#ff4444', fontWeight: 700 }}>
                    {outflow.reduce((s, r) => s + r.pln_value, 0).toFixed(2)} PLN
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard color="#00d4ff">
                <CardContent>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>Net Position</Typography>
                  <Typography variant="h4" sx={{ color: '#00d4ff', fontWeight: 700 }}>
                    {(inflow.reduce((s, r) => s + r.pln_value, 0) - outflow.reduce((s, r) => s + r.pln_value, 0)).toFixed(2)} PLN
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>

          <Paper sx={{ mb: 3, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', overflow: 'hidden' }}>
            <Box
              sx={{ px: 3, pt: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setFormExpanded((v) => !v)}
            >
              <Typography variant="h6">Add Transaction</Typography>
              <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setFormExpanded((v) => !v); }}>
                {formExpanded ? 'Hide' : 'Add'}
              </Button>
            </Box>
            <Collapse in={formExpanded}>
              <Box sx={{ px: 3, pb: 3 }}>
                <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select value={form.type} label="Type" onChange={(e) => setForm({ ...form, type: e.target.value })}>
                      <MenuItem value="inflow">Inflow</MenuItem>
                      <MenuItem value="outflow">Outflow</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date"
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select value={form.currency} label="Currency" onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                      <MenuItem value="PLN">PLN</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Amount"
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Rate"
                    type="number"
                    step="0.0001"
                    value={form.rate}
                    onChange={(e) => setForm({ ...form, rate: parseFloat(e.target.value) || 1 })}
                  />
                </Grid>
                {form.currency.toUpperCase() !== 'PLN' && (
                  <Grid item xs={12} sm={6} md={1}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={fetchRate}
                      disabled={rateLoading || !form.date}
                      startIcon={rateLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={16} />}
                    >
                      {rateLoading ? '' : 'NBP'}
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={2}>
                  <Button variant="contained" fullWidth type="submit" startIcon={<Upload size={16} />}>
                    Save
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="outlined" component="label" size="small" startIcon={<FileText size={16} />}>
                    Attach PDF
                    <input type="file" hidden accept=".pdf" onChange={(e) => setForm({ ...form, pdf: e.target.files[0] })} />
                  </Button>
                  {form.pdf && <Typography variant="caption" ml={1}>{form.pdf.name}</Typography>}
                </Grid>
                </Grid>
              </form>
                </Box>
              </Collapse>
            </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#00d4ff' }} /></Box>}

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <SectionTitle>
                <ArrowUp size={20} style={{ color: '#00ff88' }} />
                Capital Inflow (External)
              </SectionTitle>
              {renderTable(inflow, 'inflow', inflowTotal)}
            </Box>
            <Box sx={{ flex: 1 }}>
              <SectionTitle>
                <ArrowDown size={20} style={{ color: '#ff4444' }} />
                Capital Outflow (Operations)
              </SectionTitle>
              {renderTable(outflow, 'outflow', outflowTotal)}
            </Box>
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <SectionTitle>
              <Calculator size={24} style={{ color: '#00d4ff' }} />
              Tax Intelligence
            </SectionTitle>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Year</InputLabel>
              <Select value={selectedTaxYear} label="Year" onChange={(e) => { setSelectedTaxYear(e.target.value); fetchTax(e.target.value); }}>
                {taxYears.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {taxLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#00d4ff' }} /></Box>}
          {taxResult && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StatCard color="#00ff88">
                  <CardContent>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>Total Income</Typography>
                    <Typography variant="h4" sx={{ color: '#00ff88', fontWeight: 700 }}>
                      {taxResult.income.toFixed(2)} PLN
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard color="#ff4444">
                  <CardContent>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>Total Costs</Typography>
                    <Typography variant="h4" sx={{ color: '#ff4444', fontWeight: 700 }}>
                      {taxResult.costs.toFixed(2)} PLN
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard color="#b066ff">
                  <CardContent>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)' }}>Net Liability (19%)</Typography>
                    <Typography variant="h4" sx={{ color: '#b066ff', fontWeight: 700 }}>
                      {taxResult.tax.toFixed(2)} PLN
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12}>
                <StyledTableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell align="right">Value (PLN)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow><TableCell>Income (Field 22)</TableCell><TableCell align="right">{taxResult.income.toFixed(2)}</TableCell></TableRow>
                      <TableRow><TableCell>Costs incurred (Field 23)</TableCell><TableCell align="right">{taxResult.costs.toFixed(2)}</TableCell></TableRow>
                      <TableRow><TableCell>Carried-Forward Shield (Field 24)</TableCell><TableCell align="right">{taxResult.shield.toFixed(2)}</TableCell></TableRow>
                      <TableRow><TableCell>Tax Base (Field 26/27)</TableCell><TableCell align="right">{taxResult.tax_base.toFixed(2)}</TableCell></TableRow>
                      <TableRow><TableCell>Tax 19% (Field 33)</TableCell><TableCell align="right">{taxResult.tax.toFixed(2)}</TableCell></TableRow>
                      <TableRow><TableCell>New Shield / Deficit</TableCell><TableCell align="right">{taxResult.new_shield.toFixed(2)}</TableCell></TableRow>
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Box>
    <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Transaction</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select value={editForm.type} label="Type" onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}>
              <MenuItem value="inflow">Inflow</MenuItem>
              <MenuItem value="outflow">Outflow</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Date"
            type="date"
            value={editForm.date}
            onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Currency</InputLabel>
            <Select value={editForm.currency} label="Currency" onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}>
              <MenuItem value="PLN">PLN</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Amount"
            type="number"
            step="0.01"
            value={editForm.amount}
            onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })}
          />
          <TextField
            fullWidth
            size="small"
            label="Rate"
            type="number"
            step="0.0001"
            value={editForm.rate}
            onChange={(e) => setEditForm({ ...editForm, rate: parseFloat(e.target.value) || 1 })}
          />
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
          />
          <Button variant="outlined" component="label" size="small" startIcon={<FileText size={16} />}>
            Replace PDF
            <input type="file" hidden accept=".pdf" onChange={(e) => setEditForm({ ...editForm, pdf: e.target.files[0] })} />
          </Button>
          {editForm.pdf && <Typography variant="caption">{editForm.pdf.name}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdate} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default Tax;
